/**
 * Task Generator Service
 * 
 * Generates cleaning tasks from property_checklists templates based on baseline_date.
 * Uses baseline_date as zero point for frequency calculations.
 * 
 * Logic: Task occurs when the difference in days between baseline_date and target date
 * is divisible by the frequency interval.
 */

import { supabase } from '@/lib/supabase';
import type { Database } from '@/integrations/supabase/types';
import { isPlainObject } from '@/lib/checklistJson';

type PropertyChecklist = Database['public']['Tables']['property_checklists']['Row'];

type FrequencyConfig = {
  type: string;
  days?: number[];
  text?: string;
};

function parseFrequencyConfig(value: unknown): FrequencyConfig | null {
  if (!isPlainObject(value) || typeof value.type !== 'string') return null;
  return {
    type: value.type,
    days: Array.isArray(value.days)
      ? value.days.filter((day): day is number => typeof day === 'number')
      : undefined,
    text: typeof value.text === 'string' ? value.text : undefined,
  };
}

interface GenerateTasksOptions {
  daysAhead?: number; // How many days ahead to generate tasks (default: 30)
}

/**
 * Calculate next execution date based on baseline_date and frequency_config
 * Uses baseline_date as zero point instead of current date
 */
export function calculateNextExecutionDate(
  task: PropertyChecklist,
  targetDate?: Date
): string | null {
  if (!task.frequency_config) {
    return null;
  }

  const frequency = parseFrequencyConfig(task.frequency_config);
  if (!frequency) {
    return null;
  }

  const { type, days } = frequency;
  
  // Use baseline_date as zero point, fallback to created_at or current date
  const baselineDate = task.baseline_date 
    ? new Date(task.baseline_date)
    : (task.created_at ? new Date(task.created_at) : new Date());
  
  // Normalize baseline to start of day
  baselineDate.setHours(0, 0, 0, 0);
  
  // Use target date or current date
  const referenceDate = targetDate || new Date();
  referenceDate.setHours(0, 0, 0, 0);

  switch (type) {
    case 'daily': {
      // Every day: check if days difference is divisible by 1 (always true)
      const daysDiff = Math.floor((referenceDate.getTime() - baselineDate.getTime()) / (1000 * 60 * 60 * 24));
      if (daysDiff >= 0) {
        // Next occurrence is tomorrow
        const nextDate = new Date(referenceDate);
        nextDate.setDate(nextDate.getDate() + 1);
        return nextDate.toISOString().split('T')[0];
      }
      return baselineDate.toISOString().split('T')[0];
    }

    case 'work_days': {
      // Weekdays only: find next weekday
      let nextDate = new Date(referenceDate);
      nextDate.setDate(nextDate.getDate() + 1);
      
      // Skip weekends
      while (nextDate.getDay() === 0 || nextDate.getDay() === 6) {
        nextDate.setDate(nextDate.getDate() + 1);
      }
      
      return nextDate.toISOString().split('T')[0];
    }

    case 'specific_days': {
      if (!days || days.length === 0) {
        return null;
      }

      // Find next matching day of week
      let nextDate = new Date(referenceDate);
      let attempts = 0;
      
      while (attempts < 14) {
        nextDate.setDate(nextDate.getDate() + 1);
        const dayOfWeek = nextDate.getDay();
        
        // UI stores Mon=0..Sun=6; Date.getDay() is Sun=0..Sat=6
        const uiDaysAsJs = days.map((d) => (d + 1) % 7);
        if (uiDaysAsJs.includes(dayOfWeek)) {
          return nextDate.toISOString().split('T')[0];
        }
        
        attempts++;
      }
      
      return null;
    }

    case 'weekly': {
      // Every 7 days: check if days difference is divisible by 7
      const daysDiff = Math.floor((referenceDate.getTime() - baselineDate.getTime()) / (1000 * 60 * 60 * 24));
      const weeksSinceBaseline = Math.floor(daysDiff / 7);
      const nextWeekOccurrence = weeksSinceBaseline + 1;
      
      const nextDate = new Date(baselineDate);
      nextDate.setDate(nextDate.getDate() + (nextWeekOccurrence * 7));
      
      // If next occurrence is in the past or today, move to next week
      if (nextDate <= referenceDate) {
        nextDate.setDate(nextDate.getDate() + 7);
      }
      
      return nextDate.toISOString().split('T')[0];
    }

    case 'biweekly': {
      // Every 14 days: check if days difference is divisible by 14
      const daysDiff = Math.floor((referenceDate.getTime() - baselineDate.getTime()) / (1000 * 60 * 60 * 24));
      const periodsSinceBaseline = Math.floor(daysDiff / 14);
      const nextPeriodOccurrence = periodsSinceBaseline + 1;
      
      const nextDate = new Date(baselineDate);
      nextDate.setDate(nextDate.getDate() + (nextPeriodOccurrence * 14));
      
      // If next occurrence is in the past or today, move to next period
      if (nextDate <= referenceDate) {
        nextDate.setDate(nextDate.getDate() + 14);
      }
      
      return nextDate.toISOString().split('T')[0];
    }

    case 'monthly': {
      // Every month: add 1 month from baseline, then find next occurrence
      let nextDate = new Date(baselineDate);
      nextDate.setMonth(nextDate.getMonth() + 1);
      
      // Find next occurrence after reference date
      while (nextDate <= referenceDate) {
        nextDate.setMonth(nextDate.getMonth() + 1);
      }
      
      return nextDate.toISOString().split('T')[0];
    }

    case 'quarterly': {
      // Every 3 months: add 3 months from baseline, then find next occurrence
      let nextDate = new Date(baselineDate);
      nextDate.setMonth(nextDate.getMonth() + 3);
      
      // Find next occurrence after reference date
      while (nextDate <= referenceDate) {
        nextDate.setMonth(nextDate.getMonth() + 3);
      }
      
      return nextDate.toISOString().split('T')[0];
    }

    default:
      return null;
  }
}

/**
 * Check if a task should occur on a specific date based on baseline_date
 */
export function shouldTaskOccurOnDate(
  task: PropertyChecklist,
  targetDate: Date
): boolean {
  const frequency = parseFrequencyConfig(task.frequency_config);
  if (!frequency || !task.is_active) {
    return false;
  }

  const { type, days } = frequency;
  
  // Use baseline_date as zero point
  const baselineDate = task.baseline_date 
    ? new Date(task.baseline_date)
    : (task.created_at ? new Date(task.created_at) : new Date());
  
  baselineDate.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);

  const daysDiff = Math.floor((targetDate.getTime() - baselineDate.getTime()) / (1000 * 60 * 60 * 24));

  // Task cannot occur before baseline
  if (daysDiff < 0) {
    return false;
  }

  switch (type) {
    case 'daily':
      return true; // Every day after baseline

    case 'work_days':
      // Weekdays only
      return targetDate.getDay() !== 0 && targetDate.getDay() !== 6;

    case 'specific_days':
      if (!days || days.length === 0) return false;
      return days.map((d) => (d + 1) % 7).includes(targetDate.getDay());

    case 'weekly':
      // Every 7 days: days difference must be divisible by 7
      return daysDiff % 7 === 0;

    case 'biweekly':
      // Every 14 days: days difference must be divisible by 14
      return daysDiff % 14 === 0;

    case 'monthly':
      // Same day of month: check if day of month matches
      return targetDate.getDate() === baselineDate.getDate();

    case 'quarterly':
      // Every 3 months: check if it's 3, 6, 9, or 12 months from baseline
      const monthsDiff = (targetDate.getFullYear() - baselineDate.getFullYear()) * 12 
        + (targetDate.getMonth() - baselineDate.getMonth());
      return monthsDiff >= 0 && monthsDiff % 3 === 0 && targetDate.getDate() === baselineDate.getDate();

    default:
      return false;
  }
}

/**
 * Check if a task already exists for the given template and date (multi-tenant safe).
 * Idempotency: same source_template_id + same date = skip (prevents duplicates from CRON + manual).
 */
async function checkTaskExists(
  orgId: string,
  locationId: string,
  sectionId: string | null,
  taskName: string,
  scheduledDate: string,
  sourceTemplateId?: string
): Promise<boolean> {
  try {
    const dateStart = new Date(scheduledDate);
    dateStart.setHours(0, 0, 0, 0);
    const dateStartISO = dateStart.toISOString();

    const dateEnd = new Date(scheduledDate);
    dateEnd.setHours(23, 59, 59, 999);
    const dateEndISO = dateEnd.toISOString();

    if (sourceTemplateId) {
      const { data, error } = await supabase
        .from('cleaning_tasks')
        .select('id')
        .eq('org_id', orgId)
        .eq('location_id', locationId)
        .eq('source_template_id', sourceTemplateId)
        .eq('scheduled_on', scheduledDate)
        .maybeSingle();

      if (error) {
        console.error('Error checking task existence:', error);
        return false;
      }

      return Boolean(data?.id);
    }

    const { data: tasks, error } = await supabase
      .from('cleaning_tasks')
      .select('id, checklist')
      .eq('org_id', orgId)
      .eq('location_id', locationId)
      .in('task_type', ['sop_standard', 'long_term'])
      .gte('scheduled_at', dateStartISO)
      .lte('scheduled_at', dateEndISO)
      .limit(20);

    if (error) {
      console.error('Error checking task existence:', error);
      return false; // On error, assume task doesn't exist to allow creation
    }

    if (!tasks || tasks.length === 0) {
      return false;
    }

    for (const task of tasks) {
      const checklist = task.checklist;
      if (!Array.isArray(checklist) || checklist.length === 0) continue;

      const firstItem = checklist[0] as { name?: string; metadata?: { source_template_id?: string } } | undefined;
      if (!firstItem) continue;

      if (firstItem.name === taskName) {
        return true;
      }
    }

    return false;
  } catch (error) {
    console.error('Unexpected error checking task existence:', error);
    return false;
  }
}

/**
 * Generate tasks for a single property_checklist template
 */
export async function generateTasksForTemplate(
  task: PropertyChecklist,
  options: GenerateTasksOptions = {}
): Promise<{ created: number; skipped: number; errors: number }> {
  const { daysAhead = 30 } = options;

  if (!task.location_id || !task.org_id) {
    console.error('Task missing required fields:', { location_id: task.location_id, org_id: task.org_id });
    return { created: 0, skipped: 0, errors: 1 };
  }

  if (!task.is_active) {
    return { created: 0, skipped: 0, errors: 0 };
  }

  const taskName = task.name || 'Untitled Task';
  const results = { created: 0, skipped: 0, errors: 0 };

  // Generate tasks for each day in the range
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 0; i <= daysAhead; i++) {
    const targetDate = new Date(today);
    targetDate.setDate(targetDate.getDate() + i);
    targetDate.setHours(9, 0, 0, 0); // Default to 9:00 AM

    // Check if task should occur on this date
    if (!shouldTaskOccurOnDate(task, targetDate)) {
      continue;
    }

    // Check if task already exists (pass org_id + source_template_id for idempotency)
    const scheduledDateStr = targetDate.toISOString().split('T')[0];
    const exists = await checkTaskExists(
      task.org_id,
      task.location_id,
      task.section_id || null,
      taskName,
      scheduledDateStr,
      task.id
    );

    if (exists) {
      results.skipped++;
      continue;
    }

    // Determine task_type based on frequency
    const frequencyDays = getFrequencyDays(parseFrequencyConfig(task.frequency_config));
    const taskType: 'sop_standard' | 'long_term' = frequencyDays > 28 ? 'long_term' : 'sop_standard';

    // CRIT-1: Każdy element checklisty MUSI mieć id (UUID) – wymagane dla React keys, handleToggleTask i safeUpdateChecklist
    const checklist = [{
      id: crypto.randomUUID(),
      name: taskName,
      completed: false,
      metadata: {
        source_template_id: task.id,
      },
    }];

    // Create task
    try {
      const { error } = await supabase
        .from('cleaning_tasks')
        .insert({
          location_id: task.location_id,
          section_id: task.section_id || null,
          org_id: task.org_id,
          task_type: taskType,
          scheduled_at: targetDate.toISOString(),
          source_template_id: task.id,
          checklist: checklist,
          status: 'pending',
          priority: 'medium',
        });

      if (error) {
        if (error.code === '23505') {
          results.skipped++;
        } else {
          console.error('Error creating task:', error);
          results.errors++;
        }
      } else {
        results.created++;
      }
    } catch (error) {
      console.error('Unexpected error creating task:', error);
      results.errors++;
    }
  }

  return results;
}

/**
 * Get frequency in days for a frequency_config
 */
function getFrequencyDays(frequencyConfig: FrequencyConfig | null | undefined): number {
  if (!frequencyConfig) return 1;

  switch (frequencyConfig.type) {
    case 'daily':
      return 1;
    case 'work_days':
      return 1; // Daily but weekdays only
    case 'specific_days':
      return 7; // Weekly pattern
    case 'weekly':
      return 7;
    case 'biweekly':
      return 14;
    case 'monthly':
      return 30; // Approximate
    case 'quarterly':
      return 90; // Approximate
    default:
      return 1;
  }
}

/**
 * Generate tasks for all active templates in a location
 */
export async function generateTasksForLocation(
  locationId: string,
  options: GenerateTasksOptions = {}
): Promise<{ created: number; skipped: number; errors: number }> {
  try {
    const { data: templates, error } = await supabase
      .from('property_checklists')
      .select('*')
      .eq('location_id', locationId)
      .eq('is_active', true);

    if (error) {
      console.error('Error fetching templates:', error);
      return { created: 0, skipped: 0, errors: 1 };
    }

    if (!templates || templates.length === 0) {
      return { created: 0, skipped: 0, errors: 0 };
    }

    const totalResults = { created: 0, skipped: 0, errors: 0 };

    for (const template of templates) {
      const results = await generateTasksForTemplate(template as PropertyChecklist, options);
      totalResults.created += results.created;
      totalResults.skipped += results.skipped;
      totalResults.errors += results.errors;
    }

    return totalResults;
  } catch (error) {
    console.error('Unexpected error generating tasks for location:', error);
    return { created: 0, skipped: 0, errors: 1 };
  }
}
